import { useState, useRef, useEffect } from "react";
import { TbPhoto, TbX, TbUpload } from "react-icons/tb";
import { FileUploadService } from "@/services/fileUploadService";
import { SupabaseImageService } from "@/services/supabaseImageService";
import { toast } from "react-hot-toast";

interface PhotoUploaderProps {
  taskId: string;
  stepId?: string;
  currentPhotos?: string[];
  onPhotosUpdate: (photos: string[]) => void;
  maxPhotos?: number;
  disabled?: boolean;
  setUploading?: (uploading: boolean) => void;
}

export const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  taskId,
  stepId,
  currentPhotos = [],
  onPhotosUpdate,
  maxPhotos = 5,
  disabled = false,
  setUploading,
}) => {
  const [uploading, setUploadingLocal] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);
  const [signedUrls, setSignedUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch signed URLs whenever currentPhotos changes
  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (currentPhotos.length > 0) {
        const urls = await SupabaseImageService.getSignedUrls(currentPhotos);
        if (isMounted) setSignedUrls(urls);
      } else {
        setSignedUrls([]);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [currentPhotos]);

  const handleFileSelect = async (files: FileList) => {
    if (currentPhotos.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos allowed`);
      return;
    }
    setUploadingLocal(true);
    setUploading?.(true);

    const newPhotoPaths: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file type
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        // Create preview
        const previewUrl = await FileUploadService.createImagePreview(file);
        setPreviews((prev) => [...prev, previewUrl]);

        // Upload file
        let uploadResp;
        if (stepId) {
          uploadResp = await FileUploadService.uploadStepPhoto(
            taskId,
            stepId,
            file
          );
        } else {
          uploadResp = await FileUploadService.uploadTaskPhoto(taskId, file);
        }
        newPhotoPaths.push(uploadResp.path); // Store only the file path
      }

      // Update photos
      const updatedPhotos = [...currentPhotos, ...newPhotoPaths];
      onPhotosUpdate(updatedPhotos);

      toast.success(`${newPhotoPaths.length} photo(s) uploaded successfully`);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to upload photos";
      toast.error(errorMessage);
    } finally {
      setUploadingLocal(false);
      setUploading?.(false);
      setPreviews([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemovePhoto = (index: number) => {
    const updatedPhotos = currentPhotos.filter((_, i) => i !== index);
    onPhotosUpdate(updatedPhotos);
    toast.success("Photo removed");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled || uploading) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <div className="space-y-3">
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          disabled || uploading
            ? "border-gray-200 bg-gray-50 cursor-not-allowed"
            : "border-gray-300 hover:border-[#374795] hover:bg-[#374795]/10 cursor-pointer"
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          disabled={disabled || uploading}
          onChange={(e) => {
            if (e.target.files) {
              handleFileSelect(e.target.files);
            }
          }}
        />

        <div className="flex flex-col items-center space-y-2">
          {uploading ? (
            <>
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-600">Uploading photos...</p>
            </>
          ) : (
            <>
              <TbUpload size={24} className="text-gray-400" />
              <p className="text-sm text-gray-600">
                {disabled
                  ? "Photo upload not available"
                  : "Click to upload or drag and drop photos"}
              </p>
              <p className="text-xs text-gray-500">
                {currentPhotos.length}/{maxPhotos} photos
              </p>
            </>
          )}
        </div>
      </div>

      {/* Photo Grid */}
      {(currentPhotos.length > 0 || previews.length > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {/* Current Photos */}
          {signedUrls.map((url, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                <img
                  src={url}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => window.open(url, "_blank")}
                />
              </div>
              {!disabled && (
                <button
                  onClick={() => handleRemovePhoto(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove photo"
                >
                  <TbX size={12} />
                </button>
              )}
            </div>
          ))}

          {/* Preview Images (while uploading) */}
          {previews.map((preview, index) => (
            <div key={`preview-${index}`} className="relative">
              <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                <img
                  src={preview}
                  alt={`Uploading ${index + 1}`}
                  className="w-full h-full object-cover opacity-50"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              </div>
            </div>
          ))}

          {/* Add More Button */}
          {!disabled && !uploading && currentPhotos.length < maxPhotos && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="aspect-square bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-[#374795] hover:bg-[#374795]/10 transition-colors"
            >
              <TbPhoto size={24} className="text-gray-400" />
            </button>
          )}
        </div>
      )}

      {/* Photo Count Info */}
      {currentPhotos.length > 0 && (
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>{currentPhotos.length} photo(s)</span>
          {currentPhotos.length >= maxPhotos && (
            <span className="text-orange-600">Maximum photos reached</span>
          )}
        </div>
      )}
    </div>
  );
};
